/**
 * Camera math — the single source of truth for the world↔screen mapping.
 *
 * The transform is deliberately minimal: `screen = world * zoom + t`
 * (uniform scale + translate, no rotation), so the inverse is exact and
 * cheap enough to run per gesture event.
 *
 * Hot-path functions carry the `'worklet'` directive so gesture handlers
 * (M3/M4) can call them on the UI thread without JS-thread hops.
 *
 * @module floorplan/utils/camera
 * @category FloorPlan
 */

import {
  CULL_MARGIN_FACTOR,
  FIT_PADDING_PX,
  MAX_ZOOM,
  MIN_ZOOM,
} from '../constants';
import type { CameraState } from '../models/camera';
import type { Bounds, Vec2, ViewportSize } from '../models/geometry';

/** Clamp a number to `[min, max]`. */
export const clamp = (value: number, min: number, max: number): number => {
  'worklet';

  if (value < min) return min;
  if (value > max) return max;
  return value;
};

/** Clamp a zoom level to the engine limits. */
export const clampZoom = (zoom: number): number => {
  'worklet';

  return clamp(zoom, MIN_ZOOM, MAX_ZOOM);
};

/** World point → screen point. */
export const worldToScreen = (point: Vec2, camera: CameraState): Vec2 => {
  'worklet';

  return {
    x: point.x * camera.zoom + camera.tx,
    y: point.y * camera.zoom + camera.ty,
  };
};

/** Screen point → world point (inverse camera transform). */
export const screenToWorld = (point: Vec2, camera: CameraState): Vec2 => {
  'worklet';

  return {
    x: (point.x - camera.tx) / camera.zoom,
    y: (point.y - camera.ty) / camera.zoom,
  };
};

/**
 * Zoom about a screen-space focal point (pinch center / double-tap point),
 * keeping the world point under the focal point stationary — the standard
 * "zoom where my fingers are" behavior.
 */
export const zoomAboutPoint = (
  camera: CameraState,
  focalScreen: Vec2,
  nextZoomRaw: number,
): CameraState => {
  'worklet';

  const nextZoom = clampZoom(nextZoomRaw);
  // World point currently under the focal screen point.
  const worldX = (focalScreen.x - camera.tx) / camera.zoom;
  const worldY = (focalScreen.y - camera.ty) / camera.zoom;
  // Solve t so that world * nextZoom + t = focalScreen.
  return {
    zoom: nextZoom,
    tx: focalScreen.x - worldX * nextZoom,
    ty: focalScreen.y - worldY * nextZoom,
  };
};

/**
 * Camera that fits `bounds` inside `viewport` with `paddingPx` breathing
 * room, centered on both axes. Used by zoom-to-fit and zoom-to-selection.
 */
export const cameraToFitBounds = (
  bounds: Bounds,
  viewport: ViewportSize,
  paddingPx: number = FIT_PADDING_PX,
): CameraState => {
  const availableW = Math.max(viewport.width - paddingPx * 2, 1);
  const availableH = Math.max(viewport.height - paddingPx * 2, 1);
  const safeW = Math.max(bounds.width, 1);
  const safeH = Math.max(bounds.height, 1);
  const zoom = clampZoom(Math.min(availableW / safeW, availableH / safeH));
  return {
    zoom,
    tx: (viewport.width - safeW * zoom) / 2 - bounds.x * zoom,
    ty: (viewport.height - safeH * zoom) / 2 - bounds.y * zoom,
  };
};

/**
 * The world-space rect currently visible through the viewport, expanded by
 * `marginFactor` per side. The margin is what lets the recorded picture
 * absorb small pans without an immediate re-cull.
 */
export const visibleWorldBounds = (
  camera: CameraState,
  viewport: ViewportSize,
  marginFactor: number = CULL_MARGIN_FACTOR,
): Bounds => {
  const worldW = viewport.width / camera.zoom;
  const worldH = viewport.height / camera.zoom;
  const marginX = worldW * marginFactor;
  const marginY = worldH * marginFactor;
  return {
    x: -camera.tx / camera.zoom - marginX,
    y: -camera.ty / camera.zoom - marginY,
    width: worldW + marginX * 2,
    height: worldH + marginY * 2,
  };
};
